import React, {useState, useMemo, useEffect, useRef} from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
    Chip,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {BiLogoReact} from "react-icons/bi";
import ReactCourseIntro from "../../../assets/videos/ReactCourseIntro.mp4";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
/* ─── Background ─────────────────────────────────────────────────────────── */
const bgImage =
    "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ─── Styled Components — exact mirror of Linux Dashboard ───────────────── */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10,12,30,0.92), rgba(10,12,30,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(10,12,30,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.08)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "all 0.25s ease",
    "&:hover": {
        backgroundColor: "rgba(97,218,251,0.08)",
        borderColor: "gold",
        transform: "translateX(3px)",
    },
}));

const ViewButton = styled(Button)(() => ({
    backgroundColor: "gold",
    color: "#0A0C1E",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
        backgroundColor: "gold",
        transform: "scale(1.05)",
    },
}));

/* ─── Inline RadioOption ─────────────────────────────────────────────────── */
function RadioOption({ checked, onChange }) {
    return (
        <Box
            onClick={onChange}
            sx={{
                cursor: "pointer",
                color: checked ? "gold" : "rgba(255,255,255,0.25)",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
            }}
        >
            {checked
                ? <CheckCircleOutlineIcon fontSize="small" />
                : <RadioButtonUncheckedIcon fontSize="small" />}
        </Box>
    );
}

/* ─── Inline LectureTimer ────────────────────────────────────────────────── */
function LectureTimer() {
    const [seconds, setSeconds] = useState(0);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        if (!running) return;
        const id = setInterval(() => setSeconds(s => s + 1), 1000);
        return () => clearInterval(id);
    }, [running]);

    const fmt = s =>
        `${String(Math.floor(s / 3600)).padStart(2, "0")}:` +
        `${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:` +
        `${String(s % 60).padStart(2, "0")}`;

    return (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, px: 2 }}>
            <Box sx={{
                display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                justifyContent: "center",
                backgroundColor: "rgba(97,218,251,0.06)",
                border: "1px solid rgba(97,218,251,0.15)",
                borderRadius: "999px", px: 3, py: 1,
            }}>
                <Box sx={{ color: "gold", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1 }}>
                    STUDY TIMER
                </Box>
                <Box sx={{
                    fontFamily: "monospace", fontSize: "1.05rem",
                    color: "white", minWidth: "82px", textAlign: "center",
                }}>
                    {fmt(seconds)}
                </Box>
                <Button size="small" onClick={() => setRunning(r => !r)} sx={{
                    backgroundColor: running ? "rgba(255,80,80,0.12)" : "rgba(97,218,251,0.12)",
                    color: running ? "goldenrod" : "gold",
                    borderRadius: "999px", px: 2, textTransform: "none",
                    fontWeight: 700, fontSize: "0.72rem",
                    "&:hover": { backgroundColor: running ? "rgba(255,80,80,0.22)" : "rgba(97,218,251,0.22)" },
                }}>
                    {running ? "Pause" : "Start"}
                </Button>
                <Button size="small" onClick={() => { setSeconds(0); setRunning(false); }} sx={{
                    color: "rgba(255,255,255,0.35)", borderRadius: "999px",
                    px: 1.5, textTransform: "none", fontSize: "0.72rem",
                    "&:hover": { color: "white" },
                }}>
                    Reset
                </Button>
            </Box>
        </Box>
    );
}

/* ─── Inline LanguageTabs ────────────────────────────────────────────────── */
const TABS = [
    { label: "React",        path: "/react-dashboard" },
    { label: "React Native", path: "/react-native-dashboard" },
    { label: "Angular",      path: "/angular-dashboard" },
];

function LanguageTabs({ active }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4, gap: 1.5, flexWrap: "wrap", px: 2 }}>
            {TABS.map(tab => (
                <Link key={tab.label} to={tab.path} style={{ textDecoration: "none" }}>
                    <Button sx={{
                        borderRadius: "999px", px: 3, py: 0.8,
                        fontWeight: 700, fontSize: "0.8rem", textTransform: "none",
                        backgroundColor: active === tab.label ? "gold" : "rgba(255,255,255,0.05)",
                        color: active === tab.label ? "goldenrod" : "rgba(255,255,255,0.55)",
                        border: "1px solid",
                        borderColor: active === tab.label ? "transparent" : "rgba(255,255,255,0.08)",
                        "&:hover": {
                            backgroundColor: active === tab.label ? "goldenrod" : "rgba(97,218,251,0.08)",
                            color: active === tab.label ? "black" : "gold",
                            borderColor: "rgba(97,218,251,0.3)",
                        },
                    }}>
                        {tab.label}
                    </Button>
                </Link>
            ))}
        </Box>
    );
}

/* ─── React Topics — all chapters from the PDF book ─────────────────────── */
const reactTopics = [
    // Chapter 1 — Introduction
    { title: "Introduction to React & the Virtual DOM",       link: "/react/intro",                chapter: "Intro" },
    { title: "Setting Up React with Vite",                    link: "/react/setup-vite",           chapter: "Intro" },
    { title: "Setting Up React with Create React App (CRA)",  link: "/react/setup-cra",            chapter: "Intro" },
    { title: "JSX Fundamentals & Expressions",                link: "/react/jsx",                  chapter: "Intro" },
    { title: "Your First React Component",                    link: "/react/first-component",      chapter: "Intro" },
    // Chapter 2 — Components & Props
    { title: "Functional vs Class Components",                link: "/react/func-vs-class",        chapter: "Components" },
    { title: "Props — Passing Data to Components",            link: "/react/props",                chapter: "Components" },
    { title: "Default Props & PropTypes",                     link: "/react/proptypes",            chapter: "Components" },
    { title: "Children Prop & Component Composition",         link: "/react/children",             chapter: "Components" },
    { title: "Component Reusability Patterns",                link: "/react/reusability",          chapter: "Components" },
    // Chapter 3 — State & Events
    { title: "useState Hook — Local State Management",        link: "/react/usestate",             chapter: "State & Events" },
    { title: "Event Handling in React",                       link: "/react/events",               chapter: "State & Events" },
    { title: "Controlled Form Components",                    link: "/react/controlled-forms",     chapter: "State & Events" },
    { title: "Conditional Rendering",                         link: "/react/conditional",          chapter: "State & Events" },
    { title: "Rendering Lists with map() & Keys",             link: "/react/lists-keys",           chapter: "State & Events" },
    // Chapter 4 — Hooks
    { title: "useEffect — Side Effects & Data Fetching",      link: "/react/useeffect",            chapter: "Hooks" },
    { title: "useContext — Consuming Context",                link: "/react/usecontext",           chapter: "Hooks" },
    { title: "useReducer — Complex State Logic",              link: "/react/usereducer",           chapter: "Hooks" },
    { title: "useRef — DOM Access & Mutable Values",          link: "/react/useref",               chapter: "Hooks" },
    { title: "useMemo — Memoizing Expensive Calculations",    link: "/react/usememo",              chapter: "Hooks" },
    { title: "useCallback — Stable Function References",      link: "/react/usecallback",          chapter: "Hooks" },
    { title: "Custom Hooks — Reusable Stateful Logic",        link: "/react/custom-hooks",         chapter: "Hooks" },
    // Chapter 5 — Routing
    { title: "React Router v6 — Setup & Basics",              link: "/react/router-setup",         chapter: "Router" },
    { title: "Dynamic Routes & URL Parameters",               link: "/react/dynamic-routes",       chapter: "Router" },
    { title: "Nested & Child Routes",                         link: "/react/nested-routes",        chapter: "Router" },
    { title: "Protected Routes & Auth Guards",                link: "/react/protected-routes",     chapter: "Router" },
    { title: "Programmatic Navigation (useNavigate)",         link: "/react/navigate",             chapter: "Router" },
    // Chapter 6 — State Management
    { title: "Context API — Theme & Auth State",              link: "/react/context-api",          chapter: "State Mgmt" },
    { title: "Redux Toolkit — Setup & Configuration",         link: "/react/redux-setup",          chapter: "State Mgmt" },
    { title: "Redux — Actions, Reducers & Selectors",         link: "/react/redux-arc",            chapter: "State Mgmt" },
    { title: "Redux Async Thunks & API Calls",                link: "/react/redux-thunk",          chapter: "State Mgmt" },
    { title: "Zustand — Lightweight State Management",        link: "/react/zustand",              chapter: "State Mgmt" },
    // Chapter 7 — API & Async
    { title: "Fetch API & Axios Setup",                       link: "/react/axios",                chapter: "API & Async" },
    { title: "useEffect for Data Fetching Patterns",          link: "/react/data-fetching",        chapter: "API & Async" },
    { title: "TanStack Query (React Query)",                  link: "/react/react-query",          chapter: "API & Async" },
    { title: "Error Boundaries & Fallback UI",                link: "/react/error-boundaries",     chapter: "API & Async" },
    { title: "Loading Skeletons & Optimistic UI",             link: "/react/loading-states",       chapter: "API & Async" },
    // Chapter 8 — Performance
    { title: "React.memo — Preventing Re-renders",            link: "/react/react-memo",           chapter: "Performance" },
    { title: "Code Splitting with React.lazy()",              link: "/react/code-splitting",       chapter: "Performance" },
    { title: "Suspense & Lazy Loading",                       link: "/react/suspense",             chapter: "Performance" },
    { title: "Profiling with React DevTools",                 link: "/react/devtools",             chapter: "Performance" },
    // Chapter 9 — Testing
    { title: "Jest Basics for React",                         link: "/react/jest",                 chapter: "Testing" },
    { title: "React Testing Library",                         link: "/react/rtl",                  chapter: "Testing" },
    { title: "Testing User Interactions",                     link: "/react/testing-events",       chapter: "Testing" },
    { title: "Snapshot Testing",                              link: "/react/snapshots",            chapter: "Testing" },
    // Chapter 10 — Projects
    { title: "Project: Shopping Cart App",                    link: "/react/project-cart",         chapter: "Projects" },
    { title: "Project: Weather App with API",                 link: "/react/project-weather",      chapter: "Projects" },
    { title: "Project: Blog App with React Router",           link: "/react/project-blog",         chapter: "Projects" },
    { title: "Project: E-Commerce Dashboard",                 link: "/react/project-ecommerce",    chapter: "Projects" },
    { title: "Project: Real-Time Chat (Socket.io)",           link: "/react/project-chat",         chapter: "Projects" },
];

const chapterColors = {
    "Intro":         "#61DAFB",
    "Components":    "#A78BFA",
    "State & Events":"#34D399",
    "Hooks":         "#F59E0B",
    "Router":        "#F87171",
    "State Mgmt":    "#60A5FA",
    "API & Async":   "#FB923C",
    "Performance":   "#E879F9",
    "Testing":       "#4ADE80",
    "Projects":      "#FBBF24",
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function ReactDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery,   setSearchQuery]   = useState("");
    const [showScroll,    setShowScroll]    = useState(false);
    const [completed,     setCompleted]     = useState({});
    const [activeChapter, setActiveChapter] = useState("All");

    const completedCount   = Object.values(completed).filter(Boolean).length;
    const progressPercent  = (completedCount / reactTopics.length) * 100;
    const allChapters      = ["All", ...Object.keys(chapterColors)];

    /* Search + Chapter Filtering */
    const filteredTopics = useMemo(() => {
        return reactTopics.filter(t => {
            const matchSearch  = t.title.toLowerCase().includes(searchQuery.toLowerCase());
            const matchChapter = activeChapter === "All" || t.chapter === activeChapter;
            return matchSearch && matchChapter;
        });
    }, [searchQuery, activeChapter]);

    /* Scroll listener */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
    const toggleComplete  = title => setCompleted(prev => ({ ...prev, [title]: !prev[title] }));

    /*---------Video Play --------------------------------------*/
    // 1. Reference to the video DOM element to control playback
    const videoRef = useRef(null);

    // 2. State to track if video is playing (used to show/hide play icon)
    const [isPlaying, setIsPlaying] = useState(false);

    // 3. Toggle playback function
    const handleVideoClick = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    }
    /* ── RENDER ──────────────────────────────────────────────────────────── */
    return (
        <PageWrapper>

            {/* ── GOLDEN BANNER ── */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Box
                    sx={{
                        fontWeight: 800,
                        color: "gold",
                        fontSize: isMobile ? "1.5rem" : "2.8rem",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 30px rgba(97,218,251,0.25)",
                        letterSpacing: "0.04em",
                    }}
                >
                    <BiLogoReact/> MASTERING REACT
                </Box>
                <Box sx={{ mt: 1.5, color: "rgba(255,255,255,0.5)", fontSize: "0.92rem" }}>
                    Complete Web Development · React 18+ · Hooks · Router · Redux · Projects
                </Box>
                <Box sx={{ mt: 0.8, color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
                    By{" "}
                    <Box component="span" sx={{ color: "gold", fontWeight: 700 }}>
                        Ajao Hazzan
                    </Box>{" "}
                    · Full-Stack Software Engineer
                </Box>
            </Box>

            {/* ── TIMER & TABS ── */}
            <LectureTimer />
            <LanguageTabs active="React" />

            {/* ── MAIN CONTENT ── */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* ── LEFT COLUMN: Overview & Image ── */}
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Box sx={{
                            fontWeight: 700, textAlign: "center", mb: 2,
                            color: "gold", fontSize: "0.9rem", letterSpacing: "0.08em",
                        }}>
                            REACT FUNDAMENTALS
                        </Box>

                        {/* Image card — mirrors Linux Tux card */}


                        <Card sx={{
                            width: "100%",
                            background: "linear-gradient(135deg, #0D1117, #161B22)",
                            textAlign: "center",
                            padding: "15px",
                            borderRadius: "12px",
                            mt: 2,
                            border: "1px solid rgba(97,218,251,0.12)",
                        }}>
                            {/* Video Container - Handles the click interaction */}
                            <Box
                                onClick={handleVideoClick}
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    cursor: "pointer", // Indicates interactivity
                                    "&:hover .play-overlay": { backgroundColor: "rgba(0,0,0,0.4)" }
                                }}
                            >
                                <video
                                    ref={videoRef}
                                    src={ReactCourseIntro}
                                    loop
                                    playsInline
                                    /*
                                       Note: autoPlay and muted are removed to comply with
                                       browser policies for audio playback on click.
                                    */
                                    style={{
                                        width: "100%",
                                        height: isMobile ? "180px" : "220px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        display: "block",
                                    }}
                                />

                                {/* Central Play Icon Overlay (Only visible when paused) */}
                                {!isPlaying && (
                                    <Box className="play-overlay" sx={{
                                        position: "absolute",
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "rgba(0,0,0,0.2)",
                                        transition: "background-color 0.3s",
                                    }}>
                                        <PlayArrowIcon sx={{ color: "gold", fontSize: "3rem", filter: "drop-shadow(0 0 10px rgba(0,0,0,0.5))" }} />
                                    </Box>
                                )}

                                {/* Status Badge */}
                                <Box sx={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    backgroundColor: "rgba(0,0,0,0.55)",
                                    border: "1px solid rgba(97,218,251,0.3)",
                                    borderRadius: "999px",
                                    px: 1.2,
                                    py: 0.3,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    zIndex: 2
                                }}>
                                    <Box sx={{
                                        width: 6, height: 6,
                                        borderRadius: "50%",
                                        backgroundColor: isPlaying ? "#44FF44" : "#FF4444", // Green if playing
                                        animation: isPlaying ? "pulse 1.5s infinite" : "none",
                                        "@keyframes pulse": {
                                            "0%, 100%": { opacity: 1 },
                                            "50%":       { opacity: 0.3 },
                                        },
                                    }} />
                                    <Box sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.8)", fontWeight: 700 }}>
                                        {isPlaying ? "PLAYING" : "INTRO"}
                                    </Box>
                                </Box>
                            </Box>

                            <CardContent sx={{ color: "white" }}>
                                <Box sx={{ fontWeight: 800, fontSize: "1rem", mb: 0.5, color: "gold" }}>
                                    Modern Web Development
                                </Box>
                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PersonalVideoIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.5)" }} />
                                    <Box sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>
                                        Building Scalable UIs
                                    </Box>
                                </Stack>

                                <Link to="/react-playground" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            background: "linear-gradient(135deg, gold, #21C7EF)",
                                            color: "#0A0C1E",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontSize: "0.9rem",
                                            "&:hover": { background: "linear-gradient(135deg, #21C7EF, #61DAFB)" },
                                        }}
                                    >
                                        Open React Playground
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>

                        {/* Stats grid */}
                        <Grid container spacing={1.5} sx={{ mt: 2 }}>
                            {[
                                { label: "Total Topics",  value: reactTopics.length,      color: "#61DAFB" },
                                { label: "Completed",     value: completedCount,           color: "#34D399" },
                                { label: "Chapters",      value: Object.keys(chapterColors).length, color: "#A78BFA" },
                                { label: "Progress",      value: `${Math.round(progressPercent)}%`, color: "#F59E0B" },
                            ].map(s => (
                                <Grid item xs={6} key={s.label}>
                                    <Box sx={{
                                        backgroundColor: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.07)",
                                        borderRadius: "10px", p: 1.5, textAlign: "center",
                                    }}>
                                        <Box sx={{ fontSize: "1.5rem", fontWeight: 800, color: s.color }}>{s.value}</Box>
                                        <Box sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{s.label}</Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Chapter legend */}
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", mb: 1.5, fontWeight: 700, letterSpacing: "0.1em" }}>
                                CHAPTER GUIDE
                            </Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {Object.entries(chapterColors).map(([ch, col]) => (
                                    <Box
                                        key={ch}
                                        onClick={() => setActiveChapter(activeChapter === ch ? "All" : ch)}
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 0.6,
                                            cursor: "pointer",
                                            opacity: activeChapter === "All" || activeChapter === ch ? 1 : 0.4,
                                            transition: "opacity 0.2s",
                                        }}
                                    >
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: col, flexShrink: 0 }} />
                                        <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.55)" }}>{ch}</Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </DashboardGlassCard>
                </Grid>

                {/* ── RIGHT COLUMN: Curriculum with Progress & Search ── */}
                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <Box sx={{ fontWeight: 700, textAlign: "center",color:'gold', mb: 3, letterSpacing: "0.06em", fontSize: "0.9rem" }}>
                            CURRICULUM MODULES
                        </Box>

                        {/* Progress Tracker */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Box sx={{ color: "#61DAFB", fontWeight: "bold", fontSize: "0.75rem" }}>
                                    React Mastery
                                </Box>
                                <Box sx={{ color: "#61DAFB", fontWeight: "bold", fontSize: "0.75rem" }}>
                                    {Math.round(progressPercent)}%
                                </Box>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{
                                    height: 8, borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.08)",
                                    "& .MuiLinearProgress-bar": {
                                        background: "linear-gradient(90deg, #61DAFB, #21C7EF)",
                                        borderRadius: 5,
                                    },
                                }}
                            />
                            <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.3)", mt: 0.8, textAlign: "right" }}>
                                {completedCount} of {reactTopics.length} topics completed
                            </Box>
                        </Box>

                        {/* Chapter filter chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2.5 }}>
                            {allChapters.map(ch => (
                                <Chip
                                    key={ch}
                                    label={ch}
                                    size="small"
                                    onClick={() => setActiveChapter(ch)}
                                    sx={{
                                        cursor: "pointer",
                                        backgroundColor: activeChapter === ch
                                            ? (chapterColors[ch] || "#61DAFB")
                                            : "rgba(255,255,255,0.05)",
                                        color: activeChapter === ch ? "#0A0C1E" : "rgba(255,255,255,0.55)",
                                        fontWeight: activeChapter === ch ? 700 : 500,
                                        fontSize: "0.68rem",
                                        border: "1px solid",
                                        borderColor: activeChapter === ch ? "transparent" : "rgba(255,255,255,0.07)",
                                        "&:hover": {
                                            backgroundColor: chapterColors[ch]
                                                ? `${chapterColors[ch]}28`
                                                : "rgba(97,218,251,0.08)",
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Search */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search React topics..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#61DAFB", fontSize: "1.1rem" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.04)",
                                    borderRadius: "8px",
                                    fontSize: "0.88rem",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(97,218,251,0.2)" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(97,218,251,0.4)" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#61DAFB" },
                                },
                            }}
                            inputProps={{ style: { color: "white" } }}
                        />

                        {/* Topic list — scrollable */}
                        <Box sx={{
                            maxHeight: "520px", overflowY: "auto", pr: 0.5,
                            "&::-webkit-scrollbar": { width: "4px" },
                            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(97,218,251,0.25)", borderRadius: "4px" },
                        }}>
                            <Stack spacing={1.5}>
                                {filteredTopics.length > 0 ? (
                                    filteredTopics.map((topic, i) => (
                                        <TopicBox key={i}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1, minWidth: 0 }}>
                                                <RadioOption
                                                    checked={!!completed[topic.title]}
                                                    onChange={() => toggleComplete(topic.title)}
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box sx={{
                                                        fontSize: "0.85rem", fontWeight: 500,
                                                        color: completed[topic.title] ? "rgba(255,255,255,0.35)" : "white",
                                                        textDecoration: completed[topic.title] ? "line-through" : "none",
                                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    }}>
                                                        {topic.title}
                                                    </Box>
                                                    <Box sx={{
                                                        fontSize: "0.62rem", fontWeight: 600, mt: 0.2,
                                                        color: chapterColors[topic.chapter] || "#61DAFB",
                                                    }}>
                                                        {topic.chapter}
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Link to={topic.link} style={{ textDecoration: "none" }}>
                                                <ViewButton size="small">View</ViewButton>
                                            </Link>
                                        </TopicBox>
                                    ))
                                ) : (
                                    <Box sx={{ textAlign: "center", opacity: 0.4, py: 4, fontSize: "0.9rem" }}>
                                        No modules found for "{searchQuery}"
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        {/* Footer row */}
                        <Box sx={{
                            mt: 2, pt: 2,
                            borderTop: "1px solid rgba(255,255,255,0.06)",
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", flexWrap: "wrap", gap: 1,
                        }}>
                            <Box sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)" }}>
                                {filteredTopics.length} of {reactTopics.length} topics shown
                            </Box>
                            {completedCount > 0 && (
                                <Box
                                    onClick={() => setCompleted({})}
                                    sx={{ fontSize: "0.7rem", color: "rgba(255,80,80,0.5)", cursor: "pointer", "&:hover": { color: "#FF5050" } }}
                                >
                                    Reset progress
                                </Box>
                            )}
                        </Box>
                    </DashboardGlassCard>
                </Grid>
            </Grid>

            {/* ── BACK TO TOP FAB ── */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{
                        position: "fixed", bottom: 25, right: 25,
                        background: "linear-gradient(135deg, #61DAFB, #21C7EF)",
                        "&:hover": { background: "linear-gradient(135deg, #21C7EF, #61DAFB)" },
                    }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#0A0C1E" }} />
                </Fab>
            </Zoom>

        </PageWrapper>
    );
}
